import * as Sentry from '@sentry/browser'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import getProperty from 'lodash/get'
import { useEffect } from 'react'
import { LegacyHTTPDocument } from 'utils'

import useRpcClient from './useRpcClient'

export type RPCHookParams = {
  /**
   * The ID of the app to get the RPC client for
   */
  appId: string
  /**
   * The cloud domain URL for the app
   */
  cloudUrl?: string
}

export type RPCHookReturn = ReturnType<typeof useRpcClient>

/**
 * A hook that defines common RPC usage for an app. Majority of the utilities are defined
 * _outside_ of this hook, however exported from the same file.
 */
export default function useRPCHook({
  appId,
  cloudUrl,
}: RPCHookParams): RPCHookReturn {
  const rpcClient = useRpcClient(appId, cloudUrl)

  useEffect(() => {
    Sentry.addBreadcrumb({
      level: 'debug',
      data: {
        appId,
      },
    })
  }, [appId])

  return rpcClient
}

/**
 * Extract a number field from a document. If the field is not a number, `undefined` is returned
 */
export const numberField = (
  result: LegacyHTTPDocument,
  path: string,
): number | undefined => {
  const value = getProperty(result.fields, path)
  return typeof value === 'number' ? value : undefined
}

/**
 * Extract a string field from a document. If the field is not a string, an empty string
 * is returned
 */
export const stringField = (
  result: LegacyHTTPDocument,
  path: string,
): string => {
  const value = getProperty(result.fields, path)
  return typeof value === 'string' ? value : ''
}

export const enumField = <T extends string>(
  result: LegacyHTTPDocument,
  path: string,
  evaluator: (value: string) => value is T,
): T | undefined => {
  const value = getProperty(result.fields, path)
  return typeof value === 'string' && evaluator(value) ? value : undefined
}

/**
 * Extract a boolean field from a document. If the field is not a boolean, `false` is returned
 */
export const booleanField = (
  result: LegacyHTTPDocument,
  path: string,
): boolean => {
  const value = getProperty(result.fields, path)
  return typeof value === 'boolean' ? value : false
}

/**
 * Extract an object field from a document. If the field is not an object, an empty object
 * is returned
 */
export const objectField = (
  result: LegacyHTTPDocument,
  path: string,
): Record<string, unknown> => {
  const value = getProperty(result.fields, path)
  return typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

/**
 * Extract an unknown field from a document, useful for if the field type is not known
 * or is an intersection of types
 */
export const unknownField = (
  result: LegacyHTTPDocument,
  path: string,
): unknown => {
  return getProperty(result.fields, path)
}

/**
 * Parse a **valid** date string into a DayJS object. If the date is invalid, `undefined`
 * is returned
 */
export const dayJsValue = (date: string) => {
  const value = dayjs(date)
  return value.isValid() ? value : undefined
}

/**
 * Determine if an error is an unauthorized error, based on the error response.
 * This is primarily used to determine if a 401 error should be retried.
 */
export const isUnauthorizedError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 401

/**
 * Extract the error message from an error thrown by an RPC call
 *
 * @param error The error to extract the message from
 * @param defaultFallback The default message to return if the error does not have a message
 */
export const getRPCErrorMessage = (
  error: unknown,
  defaultFallback?: string,
): string => {
  const fallback =
    defaultFallback || 'An error occurred while fetching the data'

  if (isAxiosError(error)) {
    return error.response?.data?.message || fallback
  } else if (error instanceof Error) {
    return error.message || fallback
  } else {
    return fallback
  }
}
